
import "./UPIPaymentScreen.css";

import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import CartHeader from "../Cart/Header";

import PaymentRing from "../../../Assets/PaymentRing.png";
import GPayIcon from "../../../Assets/iconGPay.png";
import PaytmIcon from "../../../Assets/iconPaytm.png";
import PhonePeIcon from "../../../Assets/iconPhonepe.png";
import BhimIcon from "../../../Assets/IconBhima.png";

import { QRCodeSVG } from "qrcode.react";

export default function UPIPaymentScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  const { backendOrderId, totalAmount, qrImage } = location.state || {};

  const shortOrderId = backendOrderId
    ? (backendOrderId.length > 15
        ? `${backendOrderId.slice(0, 8)}...${backendOrderId.slice(-4)}`
        : backendOrderId)
    : '';

  const [status, setStatus] = useState("WAITING");
  const [timeLeft, setTimeLeft] = useState(120);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);

  const pollingRef = useRef(null);
  const hasPrintedRef = useRef(false);
  const receiptRef = useRef(null);

  // ✅ QR IMAGE CONVERSION
  useEffect(() => {
    if (!qrImage) return;

    try {
      // Check if it is a base64 image (like Cashfree returns)
      if (qrImage.startsWith("data:image")) {
        const byteString = atob(qrImage.split(",")[1]);
        const mimeString = qrImage.split(",")[0].split(":")[1].split(";")[0];

        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([ab], { type: mimeString });
        const url = URL.createObjectURL(blob);
        setQrBlobUrl(url);

        return () => URL.revokeObjectURL(url);
      } else {
        // PhonePe returns raw 'upi://pay...' intent string
        // We will render it using QRCode component
        setQrBlobUrl(null);
      }
    } catch (err) {
      console.error("QR conversion error:", err);
    }
  }, [qrImage]);

  // ✅ TIMER
  useEffect(() => {
    if (timeLeft <= 0) {
      clearInterval(pollingRef.current);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // ✅ PRINT FUNCTION
  const triggerPrint = () => {
    setTimeout(() => {
      try {
        const receiptHTML = receiptRef.current?.innerHTML || "";

        const html = `
        <html>
        <body style="width:180px;font-family:monospace;font-size:12px;">
        ${receiptHTML}
        </body>
        </html>
        `;

        // ANDROID DIRECT PRINT
        if (window.Android && window.Android.printReceipt) {
          window.Android.printReceipt(html);
          return;
        }

        // REACT NATIVE SUPPORT
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: "PRINT", payload: html })
          );
          return;
        }

        // WEB FALLBACK — window.print() directly (Chrome blocks popups)
        window.print();

      } catch (err) {
        console.error("Print error:", err);
        window.print();
      }
    }, 800);
  };

  // ✅ PAYMENT STATUS POLLING
  useEffect(() => {
    if (!backendOrderId) return;

    // ✅ USE ENVIRONMENT VARIABLE FOR LOCAL TESTING
    const API_URL = process.env.REACT_APP_API_URL || "https://fliplyn-b-c.fly.dev";

    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/orders/status/${backendOrderId}`
        );

        if (res.data.status === "PAID" || res.data.status === "SUCCESS") {
          setStatus("SUCCESS");
          clearInterval(pollingRef.current);

          const orderRes = await axios.get(
            `${API_URL}/orders/${backendOrderId}`
          );

          setOrderDetails(orderRes.data);
        }

        if (res.data.status === "FAILED") {
          setStatus("FAILED");
          clearInterval(pollingRef.current);
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);

    return () => clearInterval(pollingRef.current);
  }, [backendOrderId]);

  // ✅ AUTO PRINT AFTER SUCCESS
  useEffect(() => {
    if (status === "SUCCESS" && orderDetails && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      triggerPrint();
    }
  }, [status, orderDetails]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  };

  return (
    <div className="ps-page">
      <CartHeader />

      <main className="ps-container">
        <h1 className="ps-title">Scan & Pay via UPI</h1>

        <div className="ps-badge-container">
          <span className="ps-badge order-id-badge">Order ID: #{shortOrderId}</span>
        </div>

        <div className="ps-card">
          {(qrBlobUrl || qrImage) && (
            <div className="ps-ring-wrapper">
              <div className="ps-pulse-glow" />
              <img src={PaymentRing} className="ps-ring" alt="Payment ring" />
              {qrBlobUrl ? (
                <img src={qrBlobUrl} className="ps-qr" alt="UPI QR Code" />
              ) : (
                <div className="ps-qr-box">
                  <QRCodeSVG value={qrImage} size={142} />
                </div>
              )}
            </div>
          )}

          <h2 className="ps-amount">₹{totalAmount}</h2>

          <p className="ps-desc">
            Scan the QR code to pay
          </p>

          <div className="ps-timer-box">
            <span className="ps-timer-label">Time Remaining:</span>
            <span className="ps-timer">{formatTime(timeLeft)}</span>
          </div>

          <div className="ps-divider" />

          <p className="ps-accepting-text">Accepting payments from all UPI apps</p>
          <div className="ps-app-row">
            <div className="ps-app-icon-wrapper">
              <img src={GPayIcon} className="ps-app-icon" alt="Google Pay" />
            </div>
            <div className="ps-app-icon-wrapper">
              <img src={PaytmIcon} className="ps-app-icon" alt="Paytm" />
            </div>
            <div className="ps-app-icon-wrapper">
              <img src={PhonePeIcon} className="ps-app-icon" alt="PhonePe" />
            </div>
            <div className="ps-app-icon-wrapper">
              <img src={BhimIcon} className="ps-app-icon bhim" alt="BHIM UPI" />
            </div>
          </div>

          <div className={`ps-status-badge ${status.toLowerCase()}`}>
            {status === "WAITING" && (
              <>
                <span className="ps-dot-pulse" />
                Waiting for payment...
              </>
            )}
            {status === "SUCCESS" && "Payment Successful ✅"}
            {status === "FAILED" && "Payment Failed ❌"}
          </div>

          <button className="ups-home-btn" onClick={() => navigate("/")}>
            Home
          </button>
        </div>
      </main>

      {/* ✅ PRINT RECEIPT */}
{/* ✅ PRINT TOKENS */}
<div className="tk-print-root" ref={receiptRef}>     
  {orderDetails && (
    <>
      {/* ================= INVOICE TOKEN ================= */}
      <div className="tk-invoice-token">
        <p className="tk-print-header">Dine-In | Paid (UPI)</p>
        <div className="tk-divider" />

        <h2 className="tk-stall-name">
          {orderDetails.stall_name || "Tea-Topia"}
        </h2>

        <p className="tk-token-number">
          Token No: <span>{String(orderDetails.token_number).padStart(3, '0')}</span>
        </p>

        <div className="tk-divider" />

        <p className="tk-meta-line">
          Date: {formatDate(orderDetails.created_datetime)}
        </p>
        <p className="tk-meta-line">
          GSTIN: {orderDetails.gst_number || "36AAVFN1793L1ZE"}
        </p>
        <p className="tk-meta-line">
          Phone Number: {orderDetails.phone_number || "8125154931"}
        </p>

        <div className="tk-divider" />

        <div className="tk-table-header">
          <span className="tk-col-name">Item Name</span>
          <span className="tk-col-qty">Qty.</span>
          <span className="tk-col-price">Price (Rs)</span>
        </div>

        <div className="tk-divider" />

        {/* ITEMS */}
        {orderDetails.order_details.map((item, i) => (
          <div key={i} className="tk-table-row">
            <span className="tk-col-name">{item.name}</span>
            <span className="tk-col-qty">{item.quantity}</span>
            <span className="tk-col-price">{(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}

        <div className="tk-divider" />

        {/* GST */}
        <div className="tk-table-row">
          <span className="tk-col-name">CGST:</span>
          <span className="tk-col-qty"></span>
          <span className="tk-col-price">{orderDetails.cgst.toFixed(2)}</span>
        </div>

        <div className="tk-table-row">
          <span className="tk-col-name">SGST:</span>
          <span className="tk-col-qty"></span>
          <span className="tk-col-price">{orderDetails.sgst.toFixed(2)}</span>
        </div>

        <div className="tk-divider" />

        <div className="tk-table-row tk-grand-row">
          <span className="tk-col-name">Grand Total (Rs):</span>
          <span className="tk-col-qty"></span>
          <span className="tk-col-price">{orderDetails.total_amount.toFixed(2)}</span>
        </div>
      </div>

      {/* ================= SIMPLE ITEM TOKEN ================= */}
      <div className="tk-simple-token">

        <h2 className="tk-stall-name">
          {orderDetails.stall_name}
        </h2>

        <p className="tk-token-number">
          Token No: <span>{orderDetails.token_number}</span>
        </p>

        <div className="tk-divider" />

        {orderDetails.order_details.map((item, i) => (
          <div key={i} className="tk-item-row">
            <span className="tk-item-name">
              {item.name} x {item.quantity}
            </span>
            <span className="tk-item-price">
              ₹{item.price.toFixed(2)}
            </span>
          </div>
        ))}

        <div className="tk-divider" />

        <div className="tk-row tk-grand">
          <span>Total</span>
          <span>₹{orderDetails.total_amount.toFixed(2)}</span>
        </div>

      </div>
    </>
  )}
</div>
    </div>
  );
}